import axios from 'axios'
import router from "@/router";

// 👇 智能判断运行环境
function getBaseURL() {
    // 方案1：通过Capacitor判断
    if (window.Capacitor !== undefined) {
        console.log('运行环境：Capacitor APP');
        return 'https://intellilearn.top/api';  // APP环境使用线上API
    }

    // 方案2：通过构建模式判断（配合.env.app使用）
    if (process.env.VUE_APP_BUILD_MODE === 'app') {
        console.log('运行环境：APP构建模式');
        return 'https://intellilearn.top/api';
    }

    // 默认：Web环境
    console.log('运行环境：Web');
    return process.env.VUE_APP_BASEURL;
}

/**
 * 安全的 JSON 解析函数
 * 处理 SSE 格式数据和普通 JSON
 */
function safeJSONParse(str) {
    if (!str || typeof str !== 'string') {
        return str;
    }

    // 去除首尾空白
    const trimmed = str.trim();

    // 检测是否是 SSE 格式数据 (以 "data:" 开头)
    if (trimmed.startsWith('data:')) {
        console.warn('⚠️ [Request] 检测到 SSE 格式数据，跳过 JSON 解析');
        // 尝试提取 SSE 中的 JSON 数据
        const lines = trimmed.split('\n');
        for (const line of lines) {
            if (line.startsWith('data:')) {
                const jsonStr = line.slice(5).trim();
                if (jsonStr && jsonStr !== '[DONE]') {
                    try {
                        return JSON.parse(jsonStr);
                    } catch (e) {
                        // 如果解析失败，继续尝试下一行
                    }
                }
            }
        }
        // 如果无法解析，返回一个默认响应
        return { code: '200', msg: 'SSE response' };
    }

    // 检测是否是有效的 JSON 格式
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
        console.warn('⚠️ [Request] 响应不是有效的 JSON 格式:', trimmed.substring(0, 100));
        return { code: '200', msg: 'Non-JSON response', data: trimmed };
    }

    // 尝试解析 JSON
    try {
        return JSON.parse(trimmed);
    } catch (e) {
        console.error('❌ [Request] JSON 解析失败:', e.message);
        console.error('❌ [Request] 原始数据:', trimmed.substring(0, 200));
        // 返回一个默认响应，避免阻断流程
        return { code: '500', msg: 'JSON parse error', error: e.message };
    }
}

// 创建axios实例
const request = axios.create({
    baseURL: getBaseURL(),
    timeout: 1600000  // 增加超时时间到60秒
})

// request 拦截器
request.interceptors.request.use(config => {
    // 【2026-05-16 修复】区分 FormData 和普通 JSON 请求
    // FormData（如图片编辑上传）需要让浏览器自动生成
    //   'Content-Type: multipart/form-data; boundary=----...'
    // 如果在这里硬塞 'application/json'，服务端会因为缺 boundary 解析失败：
    //   org.springframework.web.multipart.MultipartException:
    //   Current request is not a multipart request
    if (config.data instanceof FormData) {
        // FormData：删除任何 Content-Type 让浏览器接管
        // 注意：必须 delete，不能设成 undefined（axios 在某些版本下处理不一致）
        delete config.headers['Content-Type'];
        delete config.headers['content-type']; // 小写也清一下，保险起见
    } else {
        config.headers['Content-Type'] = 'application/json;charset=utf-8';
    }

    let user = JSON.parse(localStorage.getItem("xm-user") || '{}')
    config.headers['token'] = user.token

    // APP环境额外配置
    if (window.Capacitor !== undefined) {
        config.headers['X-Client-Type'] = 'android-app';
        config.headers['X-App-Version'] = '1.0.0';
        config.headers['X-Platform'] = 'Android';
    }

    return config
}, error => {
    console.error('request error: ' + error)
    return Promise.reject(error)
});

// response 拦截器
request.interceptors.response.use(
    response => {
        let res = response.data;

        // 🔧 修复：使用安全的 JSON 解析
        if (typeof res === 'string') {
            res = safeJSONParse(res);
        }

        // 处理未授权
        if (res && res.code === '401') {
            router.push('/login')
        }

        return res;
    },
    error => {
        console.error('response error: ' + error)

        // 网络错误处理
        if (error.message === 'Network Error') {
            if (window.Capacitor !== undefined) {
                import('@capacitor/toast').then(({ Toast }) => {
                    Toast.show({
                        text: '网络连接失败，请检查网络设置',
                        duration: 'long',
                        position: 'center'
                    });
                });
            } else {
                console.error('网络连接失败');
            }
        }

        // 超时处理
        if (error.code === 'ECONNABORTED') {
            console.error('请求超时，请检查网络');
        }

        return Promise.reject(error)
    }
)

export default request